import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  FileText, Download, Eye, Loader2, AlertTriangle, XCircle, Check,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function formatFileSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function PublicShare() {
  const { token } = useParams();
  const [pageState, setPageState] = useState("loading"); // loading | active | inactive | expired | error
  const [share, setShare]         = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [viewing, setViewing]     = useState(null);   // docId en cours de visualisation
  const [dlIds, setDlIds]         = useState(new Set()); // docIds en cours de téléchargement
  const [bulkDl, setBulkDl]       = useState(false);
  const [error, setError]         = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/public/shares/${token}`)
      .then(async (res) => {
        if (res.ok) { setShare(await res.json()); setPageState("active"); }
        else if (res.status === 403) setPageState("inactive");
        else setPageState("expired");
      })
      .catch(() => setPageState("error"));
  }, [token]);

  /* ── Récupère l'URL signée fraîche + vérifie l'état du partage ── */
  const fetchUrl = useCallback(async (docId) => {
    const res = await fetch(`${API_BASE}/api/public/shares/${token}/download/${docId}`);
    if (!res.ok) throw res.status;
    const { url } = await res.json();
    return url;
  }, [token]);

  /* ── Visualiser : ouvrir dans un nouvel onglet ── */
  const visualise = async (docId) => {
    setViewing(docId); setError(null);
    try {
      const url = await fetchUrl(docId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (s) {
      setError(s === 403 ? "Ressource temporairement indisponible." : "Ressource introuvable.");
    } finally { setViewing(null); }
  };

  /* ── Télécharger : forcer le download via blob ── */
  const downloadBlob = async (docId, filename) => {
    const url = await fetchUrl(docId);
    const resp = await fetch(url);
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const downloadOne = async (docId, filename) => {
    setDlIds((p) => new Set([...p, docId])); setError(null);
    try { await downloadBlob(docId, filename); }
    catch (s) {
      setError(s === 403 ? "Ressource temporairement indisponible." : "Ressource introuvable.");
    } finally { setDlIds((p) => { const n = new Set(p); n.delete(docId); return n; }); }
  };

  /* ── Téléchargement groupé (séquentiel) — uniquement les documents ── */
  const downloadSelected = async () => {
    setBulkDl(true); setError(null);
    const toDownload = docItems.filter((f) => checkedIds.has(f.document_id));
    for (const f of toDownload) {
      try { await downloadBlob(f.document_id, f.filename); }
      catch (s) {
        setError(s === 403 ? "Ressource temporairement indisponible." : "Ressource introuvable.");
        break;
      }
      await new Promise((r) => setTimeout(r, 400));
    }
    setBulkDl(false);
  };

  /* ── Items & sélection ── */
  const items    = share?.items || [];
  const docItems = items.filter((i) => i.item_type === "document");
  const allChecked  = docItems.length > 0 && checkedIds.size === docItems.length;
  const someChecked = checkedIds.size > 0 && !allChecked;

  const toggleAll = () => {
    if (allChecked) setCheckedIds(new Set());
    else setCheckedIds(new Set(docItems.map((f) => f.document_id)));
  };

  const toggleOne = (docId) => {
    setCheckedIds((p) => {
      const n = new Set(p);
      n.has(docId) ? n.delete(docId) : n.add(docId);
      return n;
    });
  };

  /* ── Pages d'état ── */
  if (pageState === "loading") return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 size={24} strokeWidth={1.5} className="animate-spin text-zinc-400" />
    </div>
  );

  if (pageState === "inactive") return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="text-center max-w-sm px-6">
        <AlertTriangle size={40} strokeWidth={1} className="text-amber-400 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-zinc-800 mb-2">Partage temporairement indisponible</h1>
        <p className="text-sm text-zinc-500">Ce partage a été désactivé temporairement par son propriétaire.</p>
      </div>
    </div>
  );

  if (pageState === "expired" || pageState === "error") return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="text-center max-w-sm px-6">
        <XCircle size={40} strokeWidth={1} className="text-zinc-300 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-zinc-800 mb-2">Ressource introuvable</h1>
        <p className="text-sm text-zinc-500">Ce lien de partage est expiré ou n'existe pas.</p>
      </div>
    </div>
  );

  /* ── Partage actif ── */
  return (
    <div className="min-h-screen bg-zinc-50">

      {/* Header */}
      <header className="bg-white border-b border-zinc-200 px-6 py-5">
        <div className="max-w-[1165px] mx-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono mb-1">BeDocs · Partage</div>
          <h1 className="text-2xl font-semibold text-zinc-900">{share?.title}</h1>
          {share?.expiration_date && (
            <p className="text-xs font-mono text-zinc-400 mt-1">
              Valide jusqu'au {new Date(share.expiration_date).toLocaleDateString("fr-FR")}
            </p>
          )}
        </div>
      </header>

      <main className="max-w-[1165px] mx-auto px-6 py-8">

        {/* Bandeau d'erreur */}
        {error && (
          <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
            <AlertTriangle size={14} strokeWidth={1.5} className="shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-700">
              <XCircle size={14} />
            </button>
          </div>
        )}

        {items.length === 0 ? (
          <div className="text-center py-12 text-sm text-zinc-400">Aucun fichier dans ce partage.</div>
        ) : (
          <>
            {/* Barre d'action groupée */}
            {checkedIds.size > 0 && (
              <div
                data-testid="bulk-download-bar"
                className="mb-3 flex items-center justify-between bg-[#002FA7]/5 border border-[#002FA7]/20 px-4 py-2.5"
              >
                <span className="text-sm font-mono text-zinc-700">
                  {checkedIds.size} fichier{checkedIds.size > 1 ? "s" : ""} sélectionné{checkedIds.size > 1 ? "s" : ""}
                </span>
                <button
                  data-testid="download-selected-btn"
                  onClick={downloadSelected}
                  disabled={bulkDl}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-4 py-2 transition-colors disabled:opacity-50"
                >
                  {bulkDl ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                  {bulkDl ? "Téléchargement…" : `Télécharger (${checkedIds.size})`}
                </button>
              </div>
            )}

            {/* Tableau */}
            <div className="bg-white border border-zinc-200 overflow-hidden">
              <table className="w-full text-sm" data-testid="public-files-table">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-500">
                    <th className="w-10 px-4 py-2.5 text-center">
                      {docItems.length > 0 && (
                        <button
                          data-testid="select-all-checkbox"
                          onClick={toggleAll}
                          className={`w-4 h-4 border flex items-center justify-center mx-auto transition-colors ${
                            allChecked    ? "bg-[#002FA7] border-[#002FA7]"
                            : someChecked ? "bg-[#002FA7]/20 border-[#002FA7]"
                            : "border-zinc-300 hover:border-zinc-500"
                          }`}
                        >
                          {allChecked  && <Check size={10} strokeWidth={2.5} className="text-white" />}
                          {someChecked && <span className="w-2 h-0.5 bg-[#002FA7]" />}
                        </button>
                      )}
                    </th>
                    <th className="px-4 py-2.5 text-left font-mono">Nom du document</th>
                    <th className="px-4 py-2.5 text-right font-mono whitespace-nowrap">Pages</th>
                    <th className="px-4 py-2.5 text-right font-mono whitespace-nowrap">Taille</th>
                    <th className="px-4 py-2.5 text-center font-mono" colSpan={2}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {items.map((item) => {
                    /* ── Section ── */
                    if (item.item_type === "section") {
                      return (
                        <tr key={item.id} className="bg-zinc-50">
                          <td colSpan={6} className="px-4 py-3">
                            <span className="text-sm font-semibold uppercase tracking-[0.12em] text-zinc-700">
                              {item.content || "Section"}
                            </span>
                          </td>
                        </tr>
                      );
                    }
                    /* ── Commentaire ── */
                    if (item.item_type === "comment") {
                      return (
                        <tr key={item.id}>
                          <td colSpan={6} className="pl-10 pr-4 py-2">
                            <span className="text-sm text-zinc-500">{item.content}</span>
                          </td>
                        </tr>
                      );
                    }
                    /* ── Document ── */
                    const docId    = item.document_id;
                    const filename = item.filename;
                    const isViewing = viewing === docId;
                    const isDl      = dlIds.has(docId);
                    const isChecked = checkedIds.has(docId);
                    return (
                      <tr
                        key={item.id}
                        data-testid={`public-file-${item.id}`}
                        className={`transition-colors ${isChecked ? "bg-[#e6f1ff]" : "hover:bg-zinc-50"}`}
                      >
                        {/* Checkbox */}
                        <td className="px-4 py-3 text-center">
                          <button
                            data-testid={`check-file-${item.id}`}
                            onClick={() => toggleOne(docId)}
                            className={`w-4 h-4 border flex items-center justify-center mx-auto transition-colors ${
                              isChecked ? "bg-[#002FA7] border-[#002FA7]" : "border-zinc-300 hover:border-zinc-500"
                            }`}
                          >
                            {isChecked && <Check size={10} strokeWidth={2.5} className="text-white" />}
                          </button>
                        </td>

                        {/* Nom */}
                        <td className="py-3">
                          <div className="flex items-center gap-2 min-w-0 pl-10">
                            <FileText size={14} strokeWidth={1.5} className="text-[#DC2626] shrink-0" />
                            <span className="truncate text-sm">{filename}</span>
                          </div>
                        </td>

                        {/* Pages */}
                        <td className="px-4 py-3 text-right font-mono text-zinc-500 whitespace-nowrap">
                          {item.page_count != null ? item.page_count : "—"}
                        </td>

                        {/* Taille */}
                        <td className="px-4 py-3 text-right font-mono text-zinc-500 whitespace-nowrap">
                          {formatFileSize(item.size_bytes)}
                        </td>

                        {/* Visualiser */}
                        <td className="px-3 py-3 text-center">
                          <button
                            data-testid={`visualise-file-${item.id}`}
                            onClick={() => visualise(docId)}
                            disabled={isViewing || isDl}
                            title="Ouvrir dans le navigateur"
                            className="inline-flex items-center gap-1.5 text-xs font-medium border border-zinc-300 text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] px-3 py-1.5 transition-colors disabled:opacity-40 whitespace-nowrap"
                          >
                            {isViewing ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                            Visualiser
                          </button>
                        </td>

                        {/* Télécharger */}
                        <td className="px-3 py-3 text-center">
                          <button
                            data-testid={`download-file-${item.id}`}
                            onClick={() => downloadOne(docId, filename)}
                            disabled={isDl || isViewing}
                            title="Télécharger le fichier"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-3 py-1.5 transition-colors disabled:opacity-40 whitespace-nowrap"
                          >
                            {isDl ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                            Télécharger
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Résumé bas de page */}
            <p className="mt-3 text-xs font-mono text-zinc-400 text-right">
              {docItems.length} document{docItems.length > 1 ? "s" : ""}
              {items.length > docItems.length
                ? ` · ${items.length - docItems.length} section(s)/commentaire(s)`
                : ""}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
